import {Component, Inject, OnInit} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";

export interface EntityDetailsData {
  entity: any;
  type: string;
}

interface Evidence {
  authors: string;
  year: number;
  articleTitle: string;
  studyType: string;
  mainOutcome: string;
  precision: string;
  consistency: string;
  riskOfBias: string;
  gradeQuality: string;
}

@Component({
    selector: 'app-ckm-modal-entity-details',
    templateUrl: './ckm-modal-entity-details.component.html',
    styleUrls: ['./ckm-modal-entity-details.component.scss'],
    standalone: false
})
export class CkmModalEntityDetailsComponent implements OnInit {
  loading = false;
  biblioSearchDisplayedColumns: string[] = ['authors', 'year', 'articleTitle', 'studyType', 'mainOutcome', 'precision', 'consistency', 'riskOfBias', 'gradeQuality'];
  biblioSearchDataSource: MatTableDataSource<Evidence>;
  toolbarTitle = "";
  epistemicShowed = false;
  constructor(
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<CkmModalEntityDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EntityDetailsData
  ) {
    if (!data.entity || !data.type) {
      throw new Error('Dati mancanti: "entity" e "type" sono obbligatori.');
    }
  }

  displayedColumnsSecond = ['source', 'type', 'recency', 'corroboration', 'alignment', 'strength'];

  dataSourceSecond = [
    {
      source: 'OSINT. International Relations Analysis',
      type: 'Report',
      recency: 4,
      corroboration: 4.5,
      alignment: 'Moderate',
      strength: 'Moderate'
    },
    {
      source: 'Satellite Imagery (NATO)',
      type: 'News article',
      recency: 5,
      corroboration: 4,
      alignment: 'Strong',
      strength: 'High'
    },
    {
      source: 'Media (Open Source)',
      type: 'Confidential Memo',
      recency: 3.5,
      corroboration: 3.5,
      alignment: 'Moderate',
      strength: 'Moderate'
    },
    {
      source: 'Social Media Monitoring (OSINT)',
      type: 'Analysis',
      recency: 4,
      corroboration: 2.5,
      alignment: 'Moderate',
      strength: 'Moderate'
    }
  ];

  getStars(rating: number): string[] {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;
    return [
      ...Array(full).fill('star'),
      ...Array(half).fill('star_half'),
      ...Array(empty).fill('star_border')
    ];
  }

  getAlignmentClass(alignment: string): string {
    switch (alignment.toLowerCase()) {
      case 'strong':
        return 'icon-strong';
      case 'moderate':
        return 'icon-moderate';
      default:
        return '';
    }
  }


  displayedColumns: string[] = ['source', 'nature', 'evidence', 'bias', 'confidence'];
  dataSource = [
    {
      source: 'OSINT, International Relations Analysis',
      nature: 'Report',
      evidence: 'Unpublished analysis predicting increased tensions due to regional power shifts and unresolved disputes',
      bias: 'Moderate',
      confidence: 'HIGH'
    },
    {
      source: 'Satellite Intagery (NATO) - Yehoor Nows',
      nature: 'News Article',
      evidence: 'Recent satellite data showing military buildup near disputed border',
      bias: 'High',
      confidence: 'LOW'
    },
    {
      source: 'Social Media Monitoring (OSINT)',
      nature: 'Analysis',
      evidence: 'Aggregated findings of social media posts signal unrest and anti-government protests',
      bias: 'Moderate',
      confidence: 'MODERATE'
    }
  ];

  launchEpistemic() {
    this.loading = true;

    setTimeout(() => {
      this.loading = false;
      this.epistemicShowed = true;
    }, 2500);
  }

  ngOnInit(): void {
    switch (this.data?.type) {
      case 'action':
        this.toolbarTitle = this.data?.entity?.action?.name;
        break
      case 'event':
        this.toolbarTitle = this.data?.entity?.event?.name;
        break
      default:
        this.toolbarTitle = this.translateService.instant("generic.details");
        break;
    }
    this.biblioSearchDataSource = new MatTableDataSource<Evidence>([
        {
          "authors": "T B Birmingham, R Moyer, K Leitch, B Chesworth, D Bryant, K Willits, R Litchfield, P J Fowler, J R Giffin",
          "year": 2017,
          "articleTitle": "Changes in biomechanical risk factors for knee osteoarthritis and their association with 5-year clinically important improvement after limb realignment surgery",
          "studyType": "Studio prospettico di coorte",
          "mainOutcome": "Miglioramento significativo dei fattori biomeccanici e dei punteggi KOOS4 a 5 anni dopo osteotomia tibiale mediale",
          "precision": "95% CI per KOOS4: +14.2 (10.8, 17.6); angolo meccanico: +8.21° (7.58, 8.83); momento di adduzione: -1.49 %BW*Ht (-1.35, -1.63)",
          "consistency": "Risultati coerenti tra i soggetti con miglioramenti biomeccanici moderati",
          "riskOfBias": "Moderato; studio osservazionale con follow-up a lungo termine",
          "gradeQuality": "Moderata"
        },
        {
          "authors": "A Getgood, B Collins, K Slynarski, E Kurowska, D Parker, L Engebretsen, P B MacDonald, R Litchfield",
          "year": 2011,
          "articleTitle": "Short-term safety and efficacy of a novel high tibial osteotomy system",
          "studyType": "Studio caso-controllo multicentrico",
          "mainOutcome": "Il sistema iBalance HTO ha mostrato sicurezza ed efficacia comparabili al sistema Puddu di seconda generazione",
          "precision": "Complicazioni nel gruppo iBalance: 9.4% vs 3.1% nel gruppo di controllo; nessuna differenza significativa nei tempi di carico o unione ossea",
          "consistency": "Risultati coerenti tra i gruppi; riduzione significativa del momento di adduzione del ginocchio (P < 0.001)",
          "riskOfBias": "Moderato; confronto con serie storiche",
          "gradeQuality": "Moderata"
        },
        {
          "authors": "Jeremy Daxhelet, Tarik Aït-Si-Selmi, Jacobus H Müller, Mo Saffarini, Salvatore Ratano, Louka Bondoux, Kalin Mihov, Michel P Bonnin",
          "year": 2023,
          "articleTitle": "Custom TKA enables adequate realignment with minimal ligament release and grants satisfactory outcomes in knees that had prior osteotomies or extra-articular fracture sequelae",
          "studyType": "Studio retrospettivo di coorte",
          "mainOutcome": "La TKA personalizzata ha ottenuto riallineamento adeguato e buoni risultati clinici con minima necessità di rilascio legamentoso",
          "precision": "78% dei casi allineati entro la zona target; 95% senza necessità di rilascio legamentoso",
          "consistency": "Risultati coerenti nella maggior parte dei pazienti con precedenti osteotomie o fratture extra-articolari",
          "riskOfBias": "Moderato; studio retrospettivo con dimensione del campione limitata",
          "gradeQuality": "Moderata"
        },
        {
          "authors": "Kristyn M Leitch, Trevor B Birmingham, Cynthia E Dunning, J Robert Giffin",
          "year": 2013,
          "articleTitle": "Changes in valgus and varus alignment neutralize aberrant frontal plane knee moments in patients with unicompartmental knee osteoarthritis",
          "studyType": "Studio clinico prospettico",
          "mainOutcome": "Le osteotomie varo/valgo correggono efficacemente i momenti anomali del ginocchio nel piano frontale",
          "precision": "Cambio medio nell'impulso angolare frontale: ±0.6–0.8 %BW·Ht·s; correlazione preoperatoria MAA-R=0.87",
          "consistency": "Risultati coerenti tra pazienti con osteoartrosi unicompartimentale",
          "riskOfBias": "Basso; studio prospettico con misurazioni biomeccaniche dettagliate",
          "gradeQuality": "Alta"
        },
        {
          "authors": "T Konishi, S Hamai, H Tsushima, S Kawahara, Y Akasaki, S Yamate, S Ayukawa, Y Nakashima",
          "year": 2024,
          "articleTitle": "Restoration of anatomical knee phenotype is associated with improved clinical outcomes after total knee arthroplasty",
          "studyType": "Studio retrospettivo su larga scala",
          "mainOutcome": "Il ripristino del fenotipo anatomico del ginocchio, in particolare l'orientamento della linea articolare, è associato a migliori risultati clinici post-TKA",
          "precision": "Analisi di 1052 ginocchia osteoartritiche; miglioramenti significativi nei PROMs a 2 anni",
          "consistency": "Risultati coerenti tra i diversi fenotipi di ginocchio",
          "riskOfBias": "Moderato; studio retrospettivo con possibile variabilità nella classificazione dei fenotipi",
          "gradeQuality": "Moderata"
        }
      ]
    );
  }

  closeDialogue(event: any = null) {
    this.dialogRef.close(event);
  }
}
